/**
 * formatShortRelative — compact "3d ago" relative-time for group cards and
 * attribution rows (plan §2: `shared by @handle · 3d ago`).
 *
 * The workspace's `formatRelativeTime` renders the long form ("3 days ago");
 * the group surfaces use the terse `Nd ago` idiom. Kept local to the feature.
 *
 * Numeric parts render under tabular-nums at the call site (design.md §4).
 */
export function formatShortRelative(unixMs: number, now: number = Date.now()): string {
  if (!Number.isFinite(unixMs)) return "";
  const diffMs = now - unixMs;
  const sec = Math.floor(diffMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  const yr = Math.floor(mo / 12);
  return `${yr}y ago`;
}

/** Render a bare handle as the canonical inline `@handle` form (branding.md §8). */
export function formatHandle(handle: string | null | undefined): string {
  if (!handle || handle.trim().length === 0) return "@unknown";
  return `@${handle.trim()}`;
}

const ONE_DAY_MS = 86_400_000;

/** Whole calendar days between two instants in the viewer's local zone. */
function calendarDaysBetween(fromMs: number, toMs: number): number {
  const a = new Date(fromMs);
  a.setHours(0, 0, 0, 0);
  const b = new Date(toMs);
  b.setHours(0, 0, 0, 0);
  return Math.round((b.getTime() - a.getTime()) / ONE_DAY_MS);
}

/** "today" / "yesterday" / the terse "Nd ago" for the delivery side of the pair. */
function dayLabel(unixMs: number, now: number): string {
  const days = calendarDaysBetween(unixMs, now);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return formatShortRelative(unixMs, now);
}

/** Short weekday ("Thu") for a recent cook; falls back to "Nd ago" past a week. */
function weekdayOrRelative(unixMs: number, now: number): string {
  if (!Number.isFinite(unixMs)) return "";
  const days = calendarDaysBetween(unixMs, now);
  if (days >= 0 && days <= 6) {
    return new Date(unixMs).toLocaleDateString(undefined, { weekday: "short" });
  }
  return formatShortRelative(unixMs, now);
}

/**
 * The cook/delivery date pair for a report row (B-WP1b). A cook logged and
 * shared together renders a single "cooked 3d ago". A late "Finish the log"
 * share (delivery lags the cook by more than a day) keeps the cook's date and
 * adds the share date: "cooked Thu · shared today". `compact` is the 375px
 * form — "Thu · late" when late, else the plain cook relative.
 */
export interface CookDatePair {
  cooked: string;
  /** null unless the report was delivered >1 day after the cook. */
  shared: string | null;
  compact: string;
  isLate: boolean;
}

export function formatCookDatePair(
  cookedAt: number,
  deliveredAt: number,
  now: number = Date.now(),
): CookDatePair {
  const late = deliveredAt - cookedAt > ONE_DAY_MS;
  if (!late) {
    const rel = formatShortRelative(cookedAt, now);
    return { cooked: `cooked ${rel}`, shared: null, compact: rel, isLate: false };
  }
  const weekday = weekdayOrRelative(cookedAt, now);
  return {
    cooked: `cooked ${weekday}`,
    shared: `shared ${dayLabel(deliveredAt, now)}`,
    compact: `${weekday} · late`,
    isLate: true,
  };
}
